import { VStack, Box } from '@cad-arc';
import { InlineBanners } from '../event-panel-additional-infos/components/inline-banners';
import CreateNarrativeForm from '../event-panel-additional-infos/components/create-narrative-form';
import AdditionalInfoRenderer from '../additional-info-renderer/additional-info-renderer';
import React, { useRef } from 'react';
import { AdditionalInfoRow } from '../event-panel-additional-infos/components/additional-info-row';
import { UnreadButton } from '../event-panel-additional-infos/components/unread-button';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { EditorControl } from '../../common/components/lexical';
import { OnLexicalError } from '../../common/components/lexical/types';
import {
    AdditionalInfoNotificationManager,
    SortDirection,
    UnreadInfo,
} from '../hooks/use-additional-infos/types';
import { useAdditionalInfos } from '../hooks/use-additional-infos/additional-infos-provider';

export interface BoloPanelAdditionalInfosProps {
    boloId: number;
    boloNumber: string; // used to show agency event number in the error alert
    currentUserId: number;
    firstUnreadInfo?: UnreadInfo; // this is used by additional infos to determine where to show the unread line initially
    focusInputRef?: React.MutableRefObject<(() => void) | undefined>; // this is used by parent components to focus the narrative input
    isPanelFocused?: boolean; // this is used to determine if the unread infos in view should be marked read
    isReadOnlyMode?: boolean; // this is for a First Responder 'Narrative' tab view
    onAllRead?: (id: number) => void; // this contains a function which will be executed to let the parent know that all unreads have been seen
    onLexicalError?: OnLexicalError; // this is triggered when the lexical component throws an error, and should be used by parent to emit the error to monitoring
    notificationManager?: AdditionalInfoNotificationManager; // Adds compatibility for controlling event notifications from different sources.
    forceReload?: boolean;
}

export const BoloPanelAdditionalInfos = ({
    currentUserId,
    isPanelFocused,
    isReadOnlyMode,
    onAllRead,
    forceReload,
    onLexicalError,
}: BoloPanelAdditionalInfosProps) => {
    const editorControlRef = useRef<EditorControl>();
    const canCreateAdditionalInfos = useEnabledFeatures()?.['createAdditionalInfos'] ?? true;
    const canDeleteAdditionalInfos = useEnabledFeatures()?.['deleteAdditionalInfos'] ?? true;
    const { sortDirection } = useAdditionalInfos();
    const isInputOnBottom = sortDirection === SortDirection.ASC;
    const showNarrativeForm = !isReadOnlyMode && canCreateAdditionalInfos;
    const additionalInfoRendererProps = {
        rowComponent: AdditionalInfoRow,
        addonComponent: UnreadButton,
        currentUserId,
        markVisibleAsRead: isPanelFocused,
        canDeleteInfos: canDeleteAdditionalInfos && !isReadOnlyMode,
        onAllRead,
        forceReload,
        isReadOnlyMode,
    };

    return (
        <VStack
            align="stretch"
            height="100%"
            width="100%"
            borderLeft="sm"
            borderColor="border.default"
            bg="surface.canvas"
        >
            <InlineBanners />
            {!isInputOnBottom && showNarrativeForm && (
                <Box paddingInlineStart="4" paddingInlineEnd="4" paddingTop="2">
                    <CreateNarrativeForm
                        editorControlRef={editorControlRef}
                        onLexicalError={onLexicalError}
                    />
                </Box>
            )}
            <AdditionalInfoRenderer {...additionalInfoRendererProps} />
            {isInputOnBottom && showNarrativeForm && (
                <Box paddingInlineStart="4" paddingInlineEnd="4" paddingTop="2">
                    <CreateNarrativeForm
                        editorControlRef={editorControlRef}
                        onLexicalError={onLexicalError}
                    />
                </Box>
            )}
        </VStack>
    );
};
